import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout'
import { AsyncPipe } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav'
import { MatTooltip } from '@angular/material/tooltip'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { AngularSplitModule } from 'angular-split'
import { combineLatest, filter, forkJoin, map, mergeMap, of, switchMap, take, tap } from 'rxjs'
import { CallDetailsBarComponent } from '../call-details-bar/call-details-bar.component'
import { CallInfoComponent } from '../call-info/call-info.component'
import { CallResourcesContainerComponent } from '../call-resources-container/call-resources-container.component'
import { CallTimelineDialogComponent } from '../call-timeline-dialog/call-timeline-dialog.component'
import { Call, CallsService, canUserEdit, hasEditAccess, isDraftCall, MetricType } from '../calls.service'
import { ExportDialogComponent, ExportDialogInputData, ExportDialogOutputData } from '../export-dialog/export-dialog.component'
import { LoggerService } from '../logger.service'
import { MetricsService } from '../metrics.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToastService } from '../toast.service'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { UsersService } from '../users.service'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { ComponentWithUnsavedChanges } from '../unsaved-changes.guard'
import { CallOutputsContainerComponent } from './call-outputs-container/call-outputs-container.component'
import { EditorSettingsService } from './editor/editor-settings.service'
import { ShareCallDialogComponent } from './share-call-dialog/share-call-dialog.component'
import { TemplatesService } from '../templates.service'
import {
  BoardPackIngestDialogComponent,
  BoardPackIngestDialogOutputData,
} from '../admin/calls/admin-call-details-page/board-pack-ingest-dialog/board-pack-ingest-dialog.component'

@Component({
  selector: 'app-call',
  standalone: true,
  imports: [
    AsyncPipe,
    SpinnerComponent,
    TranslateModule,
    AngularSplitModule,
    MatDrawer,
    MatDrawerContainer,
    MatIcon,
    MatIconButton,
    MatTooltip,
    CallDetailsBarComponent,
    CallInfoComponent,
    ToolbarComponent,
    CallOutputsContainerComponent,
    CallResourcesContainerComponent,
    RouterLink,
  ],
  templateUrl: './call-page.component.html',
  styleUrl: './call-page.component.scss',
})
export class CallPageComponent implements OnInit, ComponentWithUnsavedChanges {
  router = inject(Router)
  route = inject(ActivatedRoute)
  logger = inject(LoggerService)
  dialog = inject(MatDialog)
  http = inject(HttpClient)
  callsService = inject(CallsService)
  usersService = inject(UsersService)
  toastService = inject(ToastService)
  metricsService = inject(MetricsService)
  breakpointObserver = inject(BreakpointObserver)
  templatesService = inject(TemplatesService)
  translateService = inject(TranslateService)
  editorSettingsService = inject(EditorSettingsService)

  @ViewChild(CallResourcesContainerComponent)
  callResourcesContainer: CallResourcesContainerComponent | undefined = undefined

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges) {
      event.preventDefault()
    }
  }

  get hasUnsavedChanges(): boolean {
    return this.callResourcesContainer?.hasUnsavedTemplateChanges ?? false
  }

  callId$ = this.route.params.pipe(
    map((params) => (typeof params['id'] === 'string' ? parseInt(params['id'], 10) : null)),
    filter((id) => id !== null)
  )

  call$ = this.callId$.pipe(
    switchMap((id) => this.callsService.getCall(id!)),
    tap((result) => {
      if (result.isSuccess) {
        this.metricsService.sendMetric(result.data.id, MetricType.VIEW_CALL)
        if (result.data.isUpcoming || result.data.isOngoing || isDraftCall(result.data)) {
          this.router.navigate(['/app', 'meetings', result.data.id, 'setup'], { replaceUrl: true })
        }
      }
      if (result.isError && this.currentUser$.value?.role === 'SUPER') {
        const callId = this.route.snapshot.params['id']
        this.router.navigate(['/app', 'admin', 'meetings', callId])
      }
    })
  )

  templateItems$ = this.callId$.pipe(
    switchMap((callId) => this.templatesService.getTemplateItems(callId)),
    map((result) => (result.isSuccess ? result.data : []))
  )

  isInfoPanelOpen$ = this.route.queryParams.pipe(map((params) => params['isInfoPanelOpen'] === 'true'))
  isSensitiveModeEnabled$ = this.route.queryParams.pipe(map((params) => params['useSensitiveMode'] === 'true'))
  isEditorMaximized$ = this.editorSettingsService.isEditorMaximized$
  editorMinSize = 50
  editorMaxSize = 70
  editorSize = this.editorMinSize
  useVerticalMode = false
  currentUser$ = this.usersService.currentUser$
  ingestBoardPackMutation = this.callsService.ingestBoardPackMutation()
  resendBotMutation = this.callsService.resendBotMutation()

  canUserEdit = canUserEdit

  userHasEditorAccess = hasEditAccess

  canUserComment(call: Call): boolean {
    return ['FULL_ACCESS', 'EDITOR', 'COMMENTER'].includes(call.accessLevel)
  }

  ngOnInit() {
    this.trackResponsiveBreakpoints()
    this.handleResendBotQueryParam()
  }

  toggleSensitiveMode(): void {
    this.route.queryParams
      .pipe(
        map((params) => params['useSensitiveMode'] === 'true'),
        take(1)
      )
      .subscribe((isEnabled) => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { useSensitiveMode: isEnabled ? null : true },
          queryParamsHandling: 'merge',
        })
      })
  }

  toggleInfoPanel(): void {
    this.route.queryParams
      .pipe(
        map((params) => params['isInfoPanelOpen'] === 'true'),
        take(1)
      )
      .subscribe((isOpen) => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { isInfoPanelOpen: isOpen ? null : true },
          queryParamsHandling: 'merge',
          skipLocationChange: true,
        })
      })
  }

  exportCall(call: Call): void {
    const dialogRef = this.dialog.open(ExportDialogComponent, {
      height: '365px',
      width: '450px',
      data: {
        allowTranscriptDownload: call.allowTranscriptDownload,
        allowMinutesDownload: call.allowMinutesDownload,
        allowOutlineDownload: call.allowOutlineDownload,
        hasTranscript: call.isTranscriptReady,
        hasOutline: call.isOutlineReady,
        hasActionItems: call.isOutlineReady,
        hasMinutes: call.hasMinutes,
      } as ExportDialogInputData,
    })
    const subscription = dialogRef.componentInstance.exportClicked
      .pipe(
        mergeMap((result: ExportDialogOutputData) => {
          const transcriptExport$ = result.includeTranscript ? this.callsService.exportTranscript(call, result.transcriptFormat) : of(null)
          const minutesExport$ = result.includeMinutes ? this.callsService.exportMinutes(call, result.minutesFormat) : of(null)
          const outlineExport$ = result.includeOutline ? this.callsService.exportOutline(call, result.outlineFormat) : of(null)
          const actionItemsExport$ = result.includeActionItems ? this.callsService.exportActionItems(call, result.actionItemsFormat) : of(null)
          return forkJoin([transcriptExport$, minutesExport$, outlineExport$, actionItemsExport$])
        })
      )
      .subscribe(() => {
        dialogRef.componentInstance.loading = false
        dialogRef.close()
      })
    dialogRef.afterClosed().subscribe(() => {
      subscription.unsubscribe()
    })
  }

  share(call: Call): void {
    this.dialog.open(ShareCallDialogComponent, {
      height: '700px',
      width: '700px',
      data: call,
    })
  }

  showTimeline(call: Call): void {
    this.dialog.open(CallTimelineDialogComponent, {
      width: '900px',
      maxHeight: '80vh',
      data: call,
      panelClass: 'timeline-dialog',
    })
  }

  private handleResendBotQueryParam() {
    this.route.queryParams.pipe(filter((params) => params['resendBot'] === 'true')).subscribe(() => {
      const callId = parseInt(this.route.snapshot.params['id'], 10)
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { resendBot: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      this.openResendBotDialog(callId)
    })
  }

  private openResendBotDialog(callId: number) {
    combineLatest([this.translateService.get('CALLS.RESEND_BOT.TITLE'), this.translateService.get('CALLS.RESEND_BOT.DESCRIPTION')])
      .pipe(take(1))
      .subscribe(([title, description]) => {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          data: { title, description },
        })
        dialogRef.afterClosed().subscribe(async (result: boolean) => {
          if (result) {
            await this.resendBotMutation.mutateAsync(callId)
          }
        })
      })
  }

  private trackResponsiveBreakpoints() {
    this.breakpointObserver.observe(['(max-width: 1100px)']).subscribe((result: BreakpointState) => {
      this.useVerticalMode = result.matches
    })
  }

  constructBackButtonLink(call: Call): string[] {
    if (call.accessLevel === 'FULL_ACCESS') {
      return ['/app', 'meetings', 'past']
    }
    return ['/app', 'meetings', 'shared-with-me']
  }

  ingestBoardPack(callId: number): void {
    const dialogRef = this.dialog.open(BoardPackIngestDialogComponent, {
      width: '500px',
    })

    dialogRef.afterClosed().subscribe(async (result: BoardPackIngestDialogOutputData | undefined) => {
      if (result === undefined) {
        return
      }
      await this.ingestBoardPackMutation.mutateAsync({ callId, uploadKey: result.uploadKey, fileName: result.fileName })
    })
  }
}
